import React, { useState } from 'react';
import { KernelConfig } from '../types/lenia';
import { coreKernelId, kernelDescription, kernelImageUrl } from '../utils/leniaDataAdapter';


const KernelItem: React.FC<{
  kernel: KernelConfig;
  selected: boolean;
  onSelect: (k: KernelConfig) => void;
}> = ({ kernel, selected, onSelect }) => {
  const [hasImg, setHasImg] = useState(true);

  const imgUrl = kernelImageUrl(kernel);
  const description = kernelDescription(kernel);

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(kernel);
      (e.currentTarget as HTMLDivElement).blur();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={`kernel-item${selected ? ' selected' : ''}${!hasImg ? ' noimg' : ''}`}
      onClick={() => {
        onSelect(kernel);
        (document.activeElement as HTMLElement | null)?.blur?.();
      }}
      onKeyDown={handleKey}
      title={description}
    >
      {hasImg ? (
        <div className={`kernel-thumb${selected ? ' sel' : ''}`}>
          <img
            src={imgUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="kernel-img"
            onError={() => setHasImg(false)}
          />
        </div>
      ) : (
        <div className="kernel-fallback">{description}</div>
      )}

      <div className="kernel-text">
        <div className={`kernel-description${selected ? ' accent' : ''}`}>
          {description}
        </div>
      </div>
    </div>
  );
};

export const SidebarKernelSelector: React.FC<{
  kernels: KernelConfig[];
  selectedKernel: KernelConfig | null;
  onSelect: (k: KernelConfig) => void;
}> = ({ kernels, selectedKernel, onSelect }) => {
  // Deduplicate kernels by core parameters
  // Prefer: larger R, then larger tMax, then larger grid
  const asNum = (x: any) => (typeof x === "number" ? x : Number(x) || 0);
  const isBetterVariant = (a: KernelConfig, b: KernelConfig) => {
    const aR = asNum(a.kSize), bR = asNum(b.kSize);
    if (aR !== bR) return aR > bR;

    const aT = asNum(a.tMax), bT = asNum(b.tMax);
    if (aT !== bT) return aT > bT;

    const aG = asNum(a.gridSize), bG = asNum(b.gridSize);
    return aG > bG;
  };

  const uniqueKernels = React.useMemo(() => {
    const best = new Map<string, KernelConfig>();
    for (const k of kernels) {
      const id = coreKernelId(k);
      const prev = best.get(id);
      if (!prev || isBetterVariant(k, prev)) best.set(id, k);
    }
    return Array.from(best.values());
  }, [kernels]);

  return (
    <nav className="sidebar" role="navigation" aria-label="Kernels">
      <style>{`
        :root {
          --sidebar-expanded: 20rem;
          --sidebar-collapsed: 6rem;
          --expand-duration: 500ms;
        }

        /* ===== Desktop / large screens: fixed, collapsible on hover ===== */
        .sidebar {
          position: fixed;
          left: 0;
          top: 0;
          z-index: 40;
          width: var(--sidebar-collapsed);
          min-width: 6rem;                     /* safety at extreme zoom */
          height: 100dvh;
          background: rgba(30,41,59,0.97);
          overflow-y: auto;
          border-right: 0.5rem solid #334155;
          display: flex;
          flex-direction: column;
          padding: 1rem 0;
          transition: width var(--expand-duration) ease;
          overflow-x: hidden;
          container-type: inline-size;
        }

        /* Expand only on hover — collapses when cursor leaves */
        .sidebar:hover { width: var(--sidebar-expanded); }

        .kernel-item {
          position: relative;
          display: flex;
          align-items: center;
          padding: 2rem 0.5rem;
          cursor: pointer;
          border-radius: 1rem;
          height: 5.5rem;
          box-sizing: border-box;
          scroll-snap-align: none;             /* annul snap in desktop mode */
          z-index: 1;
          gap: 0.7rem;
        }

        .sidebar:hover .kernel-item.selected { background: #1e293b; }

        /* ===== Thumbnail with inner ring & 1px crop (rem-based) ===== */
        .kernel-thumb {
          position: relative;
          width: 4.5rem;
          height: 4.5rem;
          border-radius: 1rem;
          overflow: hidden;
          background: #0b1220;
          flex: 0 0 auto;
          transition: width var(--expand-duration) ease, height var(--expand-duration) ease, box-shadow 150ms ease;
        }
        .sidebar:hover .kernel-thumb {
          width: 4.5rem;
          height: 4.5rem;
        }
        .kernel-thumb.sel {
          box-shadow:
            0 0 0 0.2rem rgba(96,165,250,0.75),
            inset 0 0 0 0.2rem rgba(96,165,250,0.25);
        }

        .kernel-img {
          display: block;
          width: calc(100% + 0.25rem); /* +4px -> 0.25rem */
          height: calc(100% + 0.25rem);
          object-fit: cover;
          transform: translate(-0.125rem, -0.125rem); /* -2px -> 0.125rem */
          filter: contrast(1.03) saturate(1.04);
        }

        /* No-image fallback */
        .kernel-item.noimg .kernel-fallback {
          width: 4.5rem;              /* fixed to rem (was 4.5px) */
          height: 4.5rem;
          border-radius: 1rem;
          display: grid;
          place-items: center;
          object-fit: cover;
          flex: 0 0 auto;
          background: #0b1220;
          color: #e2e8f0;
          font-size: 0.875rem;
          line-height: 1.1;
          padding: 0.3rem;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: normal;
        }
        .sidebar:hover .kernel-item.noimg .kernel-fallback {
          width: 4.5rem;
          height: 4.5rem;
        }

        .kernel-text {
          display: flex;
          opacity: 0;
          visibility: hidden;
          transition: opacity 250ms ease var(--expand-duration), visibility 0s linear var(--expand-duration);
          flex: 1 1 auto;
          min-width: 0;
          overflow: hidden;
          flex-direction: column;
          justify-content: center;
        }
        .sidebar:hover .kernel-text {
          opacity: 1;
          visibility: visible;
          transition-delay: 20ms;
        }

        .kernel-description {
          color: #e2e8f0;
          font-size: 1rem;
          line-height: 1.25;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
        .kernel-description.accent { color: #60a5fa; font-weight: 600; }

        .kernel-item.noimg .kernel-text {
          opacity: 1;
          visibility: visible;
          transition-delay: 0ms;
        }

        /* Container queries */
        @container (min-width: 13.125rem) {   /* 210px */
          .kernel-description { white-space: normal; line-height: 1.2; }
        }
        @container (min-width: 14.375rem) {   /* 230px */
          .kernel-description { font-size: 1rem; }
        }

        /* ===== Small screens: top horizontal scroller ===== */
        @media (max-width: 63.75rem) { 
          .sidebar {
            position: static;
            width: 100%;
            height: auto;
            border-right: none;
            border-bottom: 0.0625rem solid #334155; /* 1px */
            box-shadow: none;
            background: rgba(30,41,59,0.9);
            display: flex;
            flex-direction: row;
            gap: 0.5rem;                      /* 8px */
            padding: 0.5rem;                  /* 8px */
            overflow-x: auto;
            overflow-y: visible;
            scroll-snap-type: x proximity;
          }

          .sidebar:hover { width: 100%; }

          .sidebar::before,
          .sidebar::after { display: none; }

          .kernel-item {
            flex: 0 0 auto;
            height: 3.875rem;                 /* 62px */
            min-width: 13.125rem;             /* 210px */
            border: 0.0625rem solid rgba(148,163,184,0.18); /* 1px */
            background: #0b1220;
            padding: 0.375rem 0.5rem;         /* 6px 8px */
            scroll-snap-align: start;         /* enable snap only on mobile */
          }
          .kernel-item::after { display: none; }

          .kernel-text {
            opacity: 1;
            visibility: visible;
            transition: none;
          }

          .kernel-thumb,
          .kernel-item.noimg .kernel-fallback {
            width: 2.875rem;                  /* 46px */
            height: 2.875rem;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .sidebar { transition: none; }
          .kernel-thumb { transition: none; }
          .kernel-text { transition: none; }
        }
        /* Chrome, Edge, Safari */
        .sidebar::-webkit-scrollbar {
          width: 0.5rem;                  /* slim vertical bar */
        }
        .sidebar::-webkit-scrollbar-track {
          background: transparent;        /* let background show through */
          border-left: 0.0625rem solid #334155; /* faint line */
        }
        .sidebar::-webkit-scrollbar-thumb {
          background-color: #475569;      /* slate-600 */
          border-radius: 0.5rem;
          border: 0.125rem solid transparent; /* space inside track */
          background-clip: content-box;   /* make thumb thinner */
        }
        .sidebar::-webkit-scrollbar-thumb:hover {
          background-color: #64748b;      /* slate-500 */
        }

        /* Firefox */
        .sidebar {
          scrollbar-width: thin;
          scrollbar-color: #475569 transparent;
        }
      `}</style>

      {uniqueKernels.map(kernel => {
        const selected =
          !!(selectedKernel && coreKernelId(kernel) === coreKernelId(selectedKernel));
        return (
          <KernelItem
            key={coreKernelId(kernel)}
            kernel={kernel}
            selected={selected}
            onSelect={onSelect}
          />
        );
      })}
    </nav>
  );
};